import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { Button } from '../ui/Button';
import { WarRoomTargetCard } from './WarRoomTargetCard';
import { WarRoomTroopControls } from './WarRoomTroopControls';
import { WarRoomOutcome, OwnOffenseCard } from './WarRoomOutcome';
import { DefenseLeftAtHomeCard } from './DefenseLeftAtHomeCard';
import { EstimatedGainPanel } from './EstimatedGainPanel';
import { ConfirmAttackDialog } from './ConfirmAttackDialog';
import { buildSuccessLabel, renderTravelModifiers } from '../../hooks/useWarRoomState';
import type { WarRoomShellProps } from '../../hooks/useWarRoomState';

/**
 * SF-147 Component 1 (redesigned SF-174 Component 1): mobile-first War Room layout. `<h1>` now
 * lives at page level (`Attack.tsx`). Panel order: target card → troop controls → own-OP/defense-
 * left/outcome/estimated-gain → commit bar — troop controls now come BEFORE the outcome group
 * (were after), so editing troops never requires scrolling back up to see the result. `WarRoomOutcome`
 * drops its sticky-on-scroll behavior here (`sticky={false}`) — the reorder means it now sits
 * directly below the taller troop-controls card, where staying sticky would newly pin it over that
 * card while scrolling back up to edit rows. A compact, non-sticky verdict badge in
 * `WarRoomTroopControls`'s own header supersedes the lost "stays visible while scrolling"
 * affordance (SF-147's original acceptance criterion) without reintroducing a second sticky region.
 */
export function MobileWarRoomShell({
  province,
  targetMember,
  intel,
  onRunSohd,
  mode,
  onSwitchToAuto,
  onSwitchToManual,
  autoSwitchReason,
  generals,
  oversendPct,
  composition,
  includeSoldiers,
  includeMercenaries,
  targetOP,
  onGeneralsChange,
  onOversendChange,
  onFieldChange,
  onToggleSoldiers,
  onToggleMercenaries,
  outcome,
  attackOP,
  targetTD,
  isStale,
  isMissingIntel,
  isImpossible,
  onSendEverything,
  ownNetworth,
  militaryEffectPercentage,
  isOwnKingdomAtWar,
  ownStance,
  targetStance,
  targetDragonType,
  travelTime,
  selectedAttackType,
  isSubmitting,
  submitError,
  canLaunch,
  onLaunchClick,
  mercenaryTotalCost,
  showConfirmDialog,
  onConfirmAttack,
  onCancelConfirm,
  race,
  unitPool,
}: WarRoomShellProps) {
  const projectedLose = outcome === 'Lose';
  const targetName = targetMember ? `${targetMember.provinceName} (${targetMember.location})` : '';

  return (
    <div className="space-y-3">
      <WarRoomTargetCard
        targetMember={targetMember}
        intel={intel}
        onRunSohd={onRunSohd}
        currentTick={province.sync.currentTick}
        ownNetworth={ownNetworth}
        selectedAttackType={selectedAttackType}
      />

      <WarRoomTroopControls
        province={province}
        unitPool={unitPool}
        mode={mode}
        onSwitchToAuto={onSwitchToAuto}
        onSwitchToManual={onSwitchToManual}
        autoSwitchReason={autoSwitchReason}
        isImpossible={isImpossible}
        verdict={outcome}
        generals={generals}
        oversendPct={oversendPct}
        composition={composition}
        includeSoldiers={includeSoldiers}
        includeMercenaries={includeMercenaries}
        targetOP={targetOP}
        onGeneralsChange={onGeneralsChange}
        onOversendChange={onOversendChange}
        onFieldChange={onFieldChange}
        onToggleSoldiers={onToggleSoldiers}
        onToggleMercenaries={onToggleMercenaries}
      />

      <OwnOffenseCard attackOP={attackOP} />
      <DefenseLeftAtHomeCard province={province} composition={composition} />
      <WarRoomOutcome
        outcome={outcome}
        attackOP={attackOP}
        targetTD={targetTD}
        isStale={isStale}
        isMissingIntel={isMissingIntel}
        isImpossible={isImpossible}
        onSendEverything={onSendEverything}
        sticky={false}
      />
      <EstimatedGainPanel
        selectedAttackType={selectedAttackType}
        verdict={outcome}
        targetMember={targetMember}
        ownNetworth={ownNetworth}
        ownAcres={province.territory.totals.acres}
        attackOP={attackOP}
        targetTD={targetTD}
        militaryEffectPercentage={militaryEffectPercentage}
        isOwnKingdomAtWar={isOwnKingdomAtWar}
        ownStance={ownStance}
        targetStance={targetStance}
        targetDragonType={targetDragonType}
        intel={intel}
      />

      {/* Commit bar — attack type moved to page-level AttackTypeSelector (Component 3) */}
      <div
        className="rounded px-4 py-3 space-y-2"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        {submitError && (
          <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
        )}
        {projectedLose && (
          <div className="text-sm" style={{ color: COLORS.warning }}>
            You are projected to lose this fight.
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium" style={{ color: COLORS.text.primary }}>
            Army returns in {travelTime.finalTime} days
          </span>
          <span className="text-xs" style={{ color: COLORS.text.muted }}>
            {renderTravelModifiers(travelTime)}
          </span>
        </div>
        <Button
          onClick={onLaunchClick}
          disabled={!canLaunch}
          isLoading={isSubmitting}
          className="w-full"
          style={{ minHeight: 44 }}
        >
          Launch Attack
        </Button>
        {mercenaryTotalCost > 0 && (
          <div className="text-xs" style={{ color: COLORS.warning }}>
            Mercenary cost: {formatNumber(mercenaryTotalCost)} gc
          </div>
        )}
      </div>

      <ConfirmAttackDialog
        isOpen={showConfirmDialog}
        onConfirm={onConfirmAttack}
        onCancel={onCancelConfirm}
        attackType={selectedAttackType}
        targetName={targetName}
        armyComposition={composition}
        modifiedOffense={attackOP}
        travelTime={travelTime}
        modifiedDefense={targetTD}
        successLabel={buildSuccessLabel(outcome)}
        mercenaryTotalCost={mercenaryTotalCost}
        race={race}
      />
    </div>
  );
}
